/**
 * Test run hooks (tasks 1176–1178): one test run is an `EndpointTestRunGroup`
 * — the tests that executed together, where, and how. Every run affordance on
 * the Testing tab, a single row's Run included, goes through `useCreate…`, so
 * there is exactly one results model.
 */
import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { endpointTestRunGroupsApi, endpointTestRunsApi, endpointTestsApi } from '@/services/api';
import { trackEvent } from '@/lib/analytics';
import { saveBlobAsFile } from '@/utils/downloadFile';
import { isResultFinal } from '@/utils/testRunResults';
import { inFlightRefusal } from '@/utils/apiRefusal';
import type {
  CreateEndpointTestRunGroupInput,
  EndpointTestExportFormat,
  EndpointTestRunGroup,
  EndpointTestRunGroupDetail,
  HumanScoreInput,
} from '@/types/endpointTests';

/** How often an executing test run is re-read while results fill in. */
export const TEST_RUN_POLL_MS = 2000;

export const testRunGroupsQueryKey = (endpointId: string | undefined) =>
  ['endpoint-test-run-groups', endpointId] as const;

export const testRunGroupQueryKey = (id: string | undefined) =>
  ['endpoint-test-run-group', id] as const;

/**
 * Create AND execute a test run. The 202 carries only the id — results fill in
 * asynchronously — so the list is invalidated for the new row and the caller
 * opens it by id. Whether it is still running is the service's to say
 * (`completedAt`, task 1228), so nothing is remembered here (task 1229).
 */
export const useCreateEndpointTestRunGroup = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateEndpointTestRunGroupInput) =>
      endpointTestRunGroupsApi.create(data).then((res) => res.data.data),
    onSuccess: (_created, variables) => {
      void queryClient.invalidateQueries({ queryKey: testRunGroupsQueryKey(variables.endpointId) });
      void queryClient.invalidateQueries({ queryKey: ['endpoint-tests', variables.endpointId] });
      // `endpoint_tested` (task 427) — the saved-test half of testing. A test
      // run is accepted, not finished, when this fires, so success means the
      // run was started; results are read on the Test runs list.
      trackEvent('endpoint_tested', {
        endpoint_id: variables.endpointId,
        success: true,
        test_kind: 'saved',
      });
    },
    onError: (error, variables) => {
      // Refused because a scenario is already running there (task 1229): the
      // list is behind, so read it again.
      if (inFlightRefusal(error)) {
        void queryClient.invalidateQueries({ queryKey: testRunGroupsQueryKey(variables.endpointId) });
      }
      trackEvent('endpoint_tested', {
        endpoint_id: variables.endpointId,
        success: false,
        test_kind: 'saved',
      });
    },
  });
};

/** Filters the Test runs list sends to the service. */
export interface TestRunGroupListFilters {
  /** Narrow to test runs containing this test (sent as `endpointTestId`). */
  endpointTestId?: string;
}

/**
 * A test is running until the service stamps `completedAt` (task 1229, on task
 * 1228's column). `undefined` — a service build without the column — reads as
 * settled, as an unknown test did before.
 */
export const isTestRunning = (group: Pick<EndpointTestRunGroup, 'completedAt'>) => group.completedAt === null;

/**
 * The service stops treating an unstamped test as running after this long (task
 * 1228's `IN_FLIGHT_STALE_WINDOW_MS`: a crashed worker or a failed stamp), so the
 * app stops showing its scenarios as busy then too.
 */
export const IN_FLIGHT_STALE_WINDOW_MS = 30 * 60 * 1000;

const hasPending = (groups: EndpointTestRunGroup[] | undefined) => !!groups?.some(isTestRunning);

/**
 * The endpoint's test runs, newest first, with summary counts. Re-polls while
 * any row is still executing so its strip counts up. When a polled test moves
 * from running to settled, the scenarios list (its rows' last-run summary) and
 * those scenarios' run history are read again (task 1355): the create's
 * `onSuccess` re-read them when the test started, before it had a result.
 */
export const useEndpointTestRunGroups = (
  endpointId: string | undefined,
  filters: TestRunGroupListFilters = {},
) => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: [...testRunGroupsQueryKey(endpointId), filters],
    queryFn: async ({ queryKey }) => {
      const previous = queryClient.getQueryData<{ data?: EndpointTestRunGroup[] }>(queryKey)?.data;
      const next = await endpointTestRunGroupsApi
        .list(endpointId!, 1, 50, filters.endpointTestId)
        .then((res) => res.data);
      const wasRunning = new Set((previous ?? []).filter(isTestRunning).map((group) => group.id));
      const settled = (next.data ?? []).filter((group) => wasRunning.has(group.id) && !isTestRunning(group));
      if (settled.length > 0) {
        void queryClient.invalidateQueries({ queryKey: ['endpoint-tests', endpointId] });
        for (const testId of new Set(settled.flatMap((group) => group.testIds ?? []))) {
          void queryClient.invalidateQueries({ queryKey: ['endpoint-test-runs', testId] });
          void queryClient.invalidateQueries({ queryKey: ['endpoint-test-trend', testId] });
        }
      }
      return next;
    },
    enabled: !!endpointId,
    refetchInterval: (query) => (hasPending(query.state.data?.data) ? TEST_RUN_POLL_MS : false),
  });
};

/**
 * Settled = the service has stamped `completedAt` AND every result is final
 * (executed, and its criteria evaluated or past the grace window).
 */
export const isDetailSettled = (detail: EndpointTestRunGroupDetail | undefined, now = Date.now()) =>
  !!detail && !isTestRunning(detail) && detail.results.every((result) => isResultFinal(result, now));

/**
 * One test run with its results and scores. Polled until every result has
 * settled — the fill-in while a test run executes.
 */
export const useEndpointTestRunGroup = (id: string | undefined) =>
  useQuery({
    queryKey: testRunGroupQueryKey(id),
    queryFn: () => endpointTestRunGroupsApi.getById(id!).then((res) => res.data.data),
    enabled: !!id,
    refetchInterval: (query) => (isDetailSettled(query.state.data) ? false : TEST_RUN_POLL_MS),
  });

/**
 * The scenarios in a running test on one target (task 1229): scenario id →
 * when that test started. `environmentId` null is the working state, and only
 * null matches it, as on the service's guard. Derived from the list query the
 * Tests section already polls; a test past the stale window does not count.
 */
export const useInFlightTestIds = (
  endpointId: string | undefined,
  environmentId: string | null,
  now = Date.now(),
): Map<string, string> => {
  const { data } = useEndpointTestRunGroups(endpointId);
  const groups = data?.data;
  return useMemo(() => {
    const inFlight = new Map<string, string>();
    for (const group of groups ?? []) {
      if (!isTestRunning(group) || group.environmentId !== environmentId) continue;
      if (now - Date.parse(group.createdAt) > IN_FLIGHT_STALE_WINDOW_MS) continue;
      for (const id of group.testIds ?? []) if (!inFlight.has(id)) inFlight.set(id, group.createdAt);
    }
    return inFlight;
    // `now` is read once per render; the list's polling re-renders it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, environmentId]);
};

/** Read a test run's detail through the cache (Re-run needs its test ids). */
export const useFetchEndpointTestRunGroup = () => {
  const queryClient = useQueryClient();
  return (id: string) =>
    queryClient.fetchQuery({
      queryKey: testRunGroupQueryKey(id),
      queryFn: () => endpointTestRunGroupsApi.getById(id).then((res) => res.data.data),
    });
};

/** Soft delete; the list drops the row. */
export const useDeleteEndpointTestRunGroup = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string; endpointId: string }) =>
      endpointTestRunGroupsApi.delete(id).then((res) => res.data.data),
    onSuccess: (_deleted, variables) => {
      void queryClient.invalidateQueries({ queryKey: testRunGroupsQueryKey(variables.endpointId) });
      queryClient.removeQueries({ queryKey: testRunGroupQueryKey(variables.id) });
    },
  });
};

/**
 * Record (or replace) the human verdict on one result. The strip and row read
 * the score off the detail, so both reads are invalidated.
 */
export const useSetHumanScore = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ endpointTestRunId, data }: {
      endpointTestRunId: string;
      endpointTestRunGroupId: string;
      endpointId: string;
      data: HumanScoreInput;
    }) => endpointTestRunsApi.setHumanScore(endpointTestRunId, data).then((res) => res.data.data),
    onSuccess: (_score, variables) => {
      void queryClient.invalidateQueries({ queryKey: testRunGroupQueryKey(variables.endpointTestRunGroupId) });
      void queryClient.invalidateQueries({ queryKey: testRunGroupsQueryKey(variables.endpointId) });
      void queryClient.invalidateQueries({ queryKey: ['endpoint-test-run-scores', variables.endpointTestRunId] });
      void queryClient.invalidateQueries({ queryKey: ['endpoint-test-trend'] });
    },
  });
};

/** A test's last five results across test runs, newest first. */
export const useEndpointTestTrend = (endpointTestId: string | undefined) =>
  useQuery({
    queryKey: ['endpoint-test-trend', endpointTestId],
    queryFn: () => endpointTestsApi.trend(endpointTestId!).then((res) => res.data.data ?? []),
    enabled: !!endpointTestId,
    staleTime: 30_000,
  });

/** Export one test run's results as a file. */
export const useExportEndpointTestRunGroup = () =>
  useMutation({
    mutationFn: async ({ id, format }: { id: string; format: EndpointTestExportFormat }) => {
      const res = await endpointTestRunGroupsApi.exportFile(id, format);
      saveBlobAsFile(res.data, `test-run-${id.slice(0, 8)}.${format}`);
    },
  });

/** The version comparison report for two test runs (task 1178). */
export const useTestRunComparison = (a: string | undefined, b: string | undefined) =>
  useQuery({
    queryKey: ['endpoint-test-run-groups', 'compare', a, b],
    queryFn: () => endpointTestRunGroupsApi.compare(a!, b!).then((res) => res.data.data),
    enabled: !!a && !!b,
  });

/** The default baseline for one test run, or null when there is none. */
export const useCompareSuggestion = (id: string | undefined) =>
  useQuery({
    queryKey: ['endpoint-test-run-groups', 'compare-suggestion', id],
    queryFn: () =>
      endpointTestRunGroupsApi.compareSuggestion(id!).then((res) => res.data.data?.id ?? null),
    enabled: !!id,
  });

/** Export the comparison report as a file. */
export const useExportTestRunComparison = () =>
  useMutation({
    mutationFn: async ({ a, b, format }: { a: string; b: string; format: EndpointTestExportFormat }) => {
      const res = await endpointTestRunGroupsApi.compareExportFile(a, b, format);
      saveBlobAsFile(res.data, `test-run-comparison-${a.slice(0, 8)}-${b.slice(0, 8)}.${format}`);
    },
  });
